import React, { useState } from 'react';
import SelkirkIcon from './icons/SelkirkIcon';
import { ICON } from '../icons/iconIds';
import IconText from './icons/IconText';
import Layout from './layout/Layout';
import Card from './ui/Card';
import Button from './ui/Button';
import { deviationWizardService } from '../services/deviationWizardService';
import DeviationWizardMap from './DeviationWizardMap';
import './DeviationWizard.css';

const DeviationWizard = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [autoCalculating, setAutoCalculating] = useState(false);
  
  // Form state with default values
  const [formData, setFormData] = useState({
    easting: 385200.0,
    northing: 6945000.0,
    azimuth: 120.0,
    dip: -75.0,
    depth: 350.0,
    searchRadius: '',
    minPairs: 10,
    expand: false,
    step: 50.0,
    maxRadius: 3000.0,
    useDirectionalWeight: true,
    dirExponent: 2.0,
    useDistanceWeight: true,
    rateIntervals: '10,100'
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (value === '' ? '' : parseFloat(value) || 0)
    }));
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    setCalculating(true);
    setError(null);
    setResult(null);

    try {
      const data = await deviationWizardService.calculateDeviationCompensation({
        azDeg: formData.azimuth,
        dipDeg: formData.dip,
        easting: formData.easting,
        northing: formData.northing,
        depthM: formData.depth,
        searchRadius: formData.searchRadius,
        minPairs: formData.minPairs,
        expand: formData.expand,
        step: formData.step,
        maxRadius: formData.maxRadius,
        useDirectionalWeight: formData.useDirectionalWeight,
        dirExponent: formData.dirExponent,
        useDistanceWeight: formData.useDistanceWeight,
        rateIntervals: formData.rateIntervals
      });
      
      setResult(data);
      console.log('✅ Hexbin offset calculated:', data);
      
      // Parse GeoJSON data for map
      if (data.success && data.result) {
        const geoJsonData = {};
        
        // Check if we have expand mode response (geojson_search_area)
        if (data.result.geojson_search_area) {
          try {
            const searchArea = typeof data.result.geojson_search_area === 'string' 
              ? JSON.parse(data.result.geojson_search_area) 
              : data.result.geojson_search_area;
            geoJsonData.searchPolygon = searchArea;
          } catch (e) {
            console.error('Failed to parse geojson_search_area:', e);
          }
        }
        
        // Check if we have fixed radius response (geojson_feature)
        if (data.result.geojson_feature) {
          try {
            const feature = typeof data.result.geojson_feature === 'string'
              ? JSON.parse(data.result.geojson_feature)
              : data.result.geojson_feature;
            
            if (feature.geometry) {
              geoJsonData.searchPolygon = feature.geometry;
            }
            
            // Extract collar point from properties
            if (feature.properties && feature.properties.point) {
              geoJsonData.collarPoint = feature.properties.point;
            }
          } catch (e) {
            console.error('Failed to parse geojson_feature:', e);
          }
        }
        
        // Parse neighbor collar points
        if (data.result.geojson_neighbors) {
          try {
            const neighbors = typeof data.result.geojson_neighbors === 'string'
              ? JSON.parse(data.result.geojson_neighbors)
              : data.result.geojson_neighbors;
            geoJsonData.neighbors = neighbors;
          } catch (e) {
            console.error('Failed to parse geojson_neighbors:', e);
          }
        }
        
        // If no collar point extracted, create from input coordinates
        if (!geoJsonData.collarPoint) {
          geoJsonData.collarPoint = {
            type: 'Point',
            coordinates: [data.result.lon || formData.easting, data.result.lat || formData.northing]
          };
        }
        
        setMapData(geoJsonData);
      }
    } catch (err) {
      console.error('❌ Error calculating hexbin offset:', err);
      setError(err.message || 'Failed to calculate hexbin offset');
    } finally {
      setCalculating(false);
    }
  };

  const handleReset = () => {
    setFormData({
      easting: 385200.0,
      northing: 6945000.0,
      azimuth: 120.0,
      dip: -75.0,
      depth: 350.0,
      searchRadius: '',
      minPairs: 10,
      expand: false,
      step: 50.0,
      maxRadius: 3000.0,
      useDirectionalWeight: true,
      dirExponent: 2.0,
      useDistanceWeight: true
    });
    setResult(null);
      setError(null);
    setMapData(null);
  };

  // Handle map point selection - convert lat/lon to UTM and recalculate
  const handleMapPointSelect = async (lat, lon) => {
    // Using proj4 or a simple conversion library would be ideal
    // For now, we'll use an approximation or trigger recalculation with the clicked point
    // This is a simplified UTM conversion - in production, use proj4 library
    
    // Convert lat/lon (WGS84) to UTM Zone 8N (EPSG:26908)
    // Rough approximation for Yukon region
    const zone = 8;
    const centralMeridian = -135; // Central meridian for Zone 8
    
    // Simple conversion (this is approximate - should use proj4 in production)
    const k0 = 0.9996;
    const a = 6378137; // WGS84 major axis
    const e = 0.0818191908426; // WGS84 eccentricity
    
    const latRad = lat * Math.PI / 180;
    const lonRad = lon * Math.PI / 180;
    const lonOriginRad = centralMeridian * Math.PI / 180;
    
    const N = a / Math.sqrt(1 - Math.pow(e * Math.sin(latRad), 2));
    const T = Math.pow(Math.tan(latRad), 2);
    const C = (Math.pow(e, 2) / (1 - Math.pow(e, 2))) * Math.pow(Math.cos(latRad), 2);
    const A = (lonRad - lonOriginRad) * Math.cos(latRad);
    
    const M = a * ((1 - Math.pow(e, 2) / 4 - 3 * Math.pow(e, 4) / 64 - 5 * Math.pow(e, 6) / 256) * latRad -
                   (3 * Math.pow(e, 2) / 8 + 3 * Math.pow(e, 4) / 32 + 45 * Math.pow(e, 6) / 1024) * Math.sin(2 * latRad) +
                   (15 * Math.pow(e, 4) / 256 + 45 * Math.pow(e, 6) / 1024) * Math.sin(4 * latRad) -
                   (35 * Math.pow(e, 6) / 3072) * Math.sin(6 * latRad));
    
    const easting = k0 * N * (A + (1 - T + C) * Math.pow(A, 3) / 6 + 
                              (5 - 18 * T + Math.pow(T, 2) + 72 * C - 58 * (Math.pow(e, 2) / (1 - Math.pow(e, 2)))) * 
                              Math.pow(A, 5) / 120) + 500000;
    
    const northing = k0 * (M + N * Math.tan(latRad) * (Math.pow(A, 2) / 2 + 
                           (5 - T + 9 * C + 4 * Math.pow(C, 2)) * Math.pow(A, 4) / 24 + 
                           (61 - 58 * T + Math.pow(T, 2) + 600 * C - 330 * (Math.pow(e, 2) / (1 - Math.pow(e, 2)))) * 
                           Math.pow(A, 6) / 720));
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      easting: parseFloat(easting.toFixed(2)),
      northing: parseFloat(northing.toFixed(2))
    }));

    // Auto-trigger calculation
    setAutoCalculating(true);
    try {
      const data = await deviationWizardService.calculateDeviationCompensation({
        azDeg: formData.azimuth,
        dipDeg: formData.dip,
        easting: easting,
        northing: northing,
        depthM: formData.depth,
        searchRadius: formData.searchRadius,
        minPairs: formData.minPairs,
        expand: formData.expand,
        step: formData.step,
        maxRadius: formData.maxRadius,
        useDirectionalWeight: formData.useDirectionalWeight,
        dirExponent: formData.dirExponent,
        useDistanceWeight: formData.useDistanceWeight
      });
      
      setResult(data);
      
      // Parse GeoJSON data for map
      if (data.success && data.result) {
        const geoJsonData = {};
        
        if (data.result.geojson_search_area) {
          try {
            const searchArea = typeof data.result.geojson_search_area === 'string' 
              ? JSON.parse(data.result.geojson_search_area) 
              : data.result.geojson_search_area;
            geoJsonData.searchPolygon = searchArea;
          } catch (e) {
            console.error('Failed to parse geojson_search_area:', e);
          }
        }
        
        if (data.result.geojson_feature) {
          try {
            const feature = typeof data.result.geojson_feature === 'string'
              ? JSON.parse(data.result.geojson_feature)
              : data.result.geojson_feature;
            
            if (feature.geometry) {
              geoJsonData.searchPolygon = feature.geometry;
            }
            
            if (feature.properties && feature.properties.point) {
              geoJsonData.collarPoint = feature.properties.point;
            }
          } catch (e) {
            console.error('Failed to parse geojson_feature:', e);
          }
        }
        
        // Parse neighbor collar points
        if (data.result.geojson_neighbors) {
          try {
            const neighbors = typeof data.result.geojson_neighbors === 'string'
              ? JSON.parse(data.result.geojson_neighbors)
              : data.result.geojson_neighbors;
            geoJsonData.neighbors = neighbors;
          } catch (e) {
            console.error('Failed to parse geojson_neighbors:', e);
          }
        }
        
        if (!geoJsonData.collarPoint) {
          geoJsonData.collarPoint = {
            type: 'Point',
            coordinates: [lon, lat]
          };
        }
        
        setMapData(geoJsonData);
      }
    } catch (err) {
      console.error('Auto-calculation error:', err);
    } finally {
      setAutoCalculating(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshMessage(null);
    setError(null);

    try {
      const data = await deviationWizardService.refreshDeviationMetrics();
      
      setRefreshMessage({
        type: 'success',
        text: data.message || 'Deviation metrics refreshed successfully'
      });
      console.log('✅ Deviation metrics refreshed:', data);
      
      // Clear success message after 5 seconds
      setTimeout(() => setRefreshMessage(null), 5000);
    } catch (err) {
      console.error('❌ Error refreshing deviation metrics:', err);
      setError(err.message || 'Failed to refresh deviation metrics');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Layout user={user} currentRoute="/deviation-wizard">
      <div className="deviation-wizard">
        <div className="deviation-wizard-header">
          <div className="header-content">
            <div>
          <IconText icon={ICON.appDeviationWizard} as="h1">Deviation Wizard</IconText>
              <p>Pre-compensate drill hole plans using spatially-binned historical deviation patterns</p>
            </div>
            <Button 
              variant="secondary"
              onClick={handleRefresh}
              disabled={refreshing || calculating}
              loading={refreshing}
              className="refresh-button"
            >
              {refreshing ? 'Refreshing...' : (<IconText icon={ICON.uiRefresh} iconSize={16}>Refresh Deviation Data</IconText>)}
            </Button>
          </div>
          
          {/* Refresh Success Message */}
          {refreshMessage && (
            <div className={`refresh-message ${refreshMessage.type}`}>
              <><SelkirkIcon name={ICON.uiSuccess} size={16} decorative /> {refreshMessage.text}</>
            </div>
          )}
        </div>

        {/* Calculator Card */}
        <Card className="calculator-card">
          <div className="calculator-header-row">
            <div>
              <IconText icon={ICON.uiCalculator} as="h2">Drillhole Deviation Pre-Compensator</IconText>
              <p className="calculator-description">
                Enter planned collar coordinates and orientation to calculate compensated values based on historical deviation patterns.
              </p>
            </div>
            <button 
              type="button" 
              className="info-button"
              onClick={() => setShowInfo(!showInfo)}
              title="View methodology"
            >
              <IconText icon={ICON.uiTip} iconSize={14}>Info</IconText>
            </button>
          </div>

          {/* Methodology Info Panel */}
          {showInfo && (
            <div className="info-panel">
              <IconText icon={ICON.uiClipboardList} as="h3">Methodology: Hexbin-Based Deviation Compensation</IconText>
              
              <div className="info-section">
                <h4>Objective</h4>
                <p>
                  Generate systematic azimuth/dip adjustments for planned drillholes so that expected 
                  downhole deviation is pre-compensated and holes land closer to their intended targets.
                </p>
              </div>

              <div className="info-section">
                <h4>How It Works</h4>
                <ol>
                  <li>
                    <strong>Historical Analysis:</strong> Calculate deviation patterns (Δaz, Δdip) from 
                    thousands of completed holes by comparing start vs. end survey measurements.
                  </li>
                  <li>
                    <strong>Radius-Based Search:</strong> For each planned collar location, search for 
                    historical holes within expanding radii (500m, 1000m, 1500m, property-wide) until 
                    sufficient data is found.
                  </li>
                  <li>
                    <strong>Dip Classification:</strong> Categorize holes into steep (≥60°), moderate (30-59°), 
                    or shallow (&lt;30°) bins, as deviation behavior varies by hole angle.
                  </li>
                  <li>
                    <strong>Depth Classification:</strong> Group holes by target depth ranges (0-200m, 
                    200-400m, 400-600m, ≥600m) as deviation magnitude increases with depth.
                  </li>
                  <li>
                    <strong>Statistical Aggregation:</strong> Calculate median Δdip and circular mean Δaz 
                    for each (radius, dip_bin, depth_bin) combination from historical data, stored in 
                    materialized views for fast lookup.
                  </li>
                  <li>
                    <strong>Pre-Compensation:</strong> For your planned hole, retrieve the expected deviation 
                    statistics for that location, angle, and depth, then adjust the starting orientation:
                    <ul>
                      <li>dip_compensated = dip_planned − median_Δdip</li>
                      <li>az_compensated = (az_planned − mean_Δaz) mod 360</li>
                    </ul>
                  </li>
                </ol>
              </div>

              <div className="info-section">
                <h4>Search Radius Parameters</h4>
                <p>
                  <strong>What is it?</strong> Optional starting search radius in meters. If left empty, the system 
                  uses an expanding radius search strategy.
                </p>
                <p>
                  <strong>Default behavior:</strong> When not specified, the system starts at 500m and expands 
                  automatically until enough historical data is found.
                </p>
                <p>
                  <strong>When to specify:</strong> Use a specific radius when you want to limit the search to 
                  a particular area or need faster results from a known good location.
                </p>
                <p>
                  <strong>Recommended values:</strong> 500m for local patterns, 1000m for broader area, 
                  1500m for regional trends.
                </p>
              </div>

              <div className="info-section">
                <h4>Expand Mode</h4>
                <p>
                  <strong>What it does:</strong> Automatically expands search radius until the minimum number 
                  of historical pairs is found.
                </p>
                <p>
                  <strong>When enabled:</strong> System starts at your search radius (or 500m default) and 
                  expands by the step size until it finds enough historical data or reaches the maximum radius.
                </p>
                <p><strong>Parameters explained:</strong></p>
                <ul>
                  <li>
                    <strong>Min Pairs:</strong> Target minimum number of historical drill holes to use. 
                    More pairs = better statistics, but may require larger search radius.
                  </li>
                  <li>
                    <strong>Step:</strong> Distance to increase radius on each expansion. Smaller steps = 
                    more precise, but more iterations. Recommended: 250m.
                  </li>
                  <li>
                    <strong>Max Radius:</strong> Maximum search distance before giving up. Prevents searching 
                    the entire property. Recommended: 3000m.
                  </li>
                </ul>
                <p>
                  <strong>Recommended settings:</strong> Start: 500m, Min Pairs: 25, Step: 250m, Max: 3000m
                </p>
              </div>

              <div className="info-section">
                <h4>Fixed Radius Mode (Expand Off)</h4>
                <p>
                  <strong>When to use:</strong> When you want to see what historical data exists within a 
                  specific distance only.
                </p>
                <p>
                  <strong>Behavior:</strong> Uses exactly the radius you specify, doesn't expand even if 
                  fewer pairs are found.
                </p>
                <p>
                  <strong>Trade-off:</strong> May have low n_pairs (less reliable) but gives you control 
                  over the search area.
                </p>
              </div>

              <div className="info-section">
                <h4>How to Choose Search Mode</h4>
                <ul>
                  <li>
                    <strong>Use Expand Mode (default):</strong> When you want the best statistical reliability 
                    and don't mind larger search areas.
                  </li>
                  <li>
                    <strong>Use Fixed Radius:</strong> When you need to stay within a specific geological zone 
                    or want faster results from a known good area.
                  </li>
                </ul>
              </div>

              <div className="info-section">
                <h4>Directional & Distance Weighting</h4>
                <p>
                  <strong>What it does:</strong> Intelligently prioritizes historical holes based on their relevance 
                  to your planned hole's drilling direction and proximity.
                </p>
                <p><strong>Distance Weighting:</strong></p>
                <ul>
                  <li>Closer holes contribute more to the calculation than distant holes</li>
                  <li>Weight decreases with distance using inverse-square law</li>
                  <li>Toggle on/off to compare local vs. regional patterns</li>
                </ul>
                <p><strong>Directional Weighting:</strong></p>
                <ul>
                  <li>Holes drilled in similar directions are weighted more heavily</li>
                  <li>Uses angular alignment: holes within ±45° of your planned azimuth get higher weight</li>
                  <li>
                    <strong>Exponent slider (1-4):</strong> Controls how aggressively to favor aligned holes:
                    <ul>
                      <li>1.0 = mild directional preference (broader influence)</li>
                      <li>2.0 = moderate (default, balanced)</li>
                      <li>4.0 = strong preference (only closely aligned holes matter)</li>
                    </ul>
                  </li>
                  <li>Toggle off to use all holes regardless of drilling direction</li>
                </ul>
                <p>
                  <strong>Combined weighting:</strong> Total weight = distance_weight × directional_weight. 
                  Historical holes with total weight = 0 are excluded from the calculation (shown as grey dots on map).
                </p>
              </div>

              <div className="info-section">
                <h4>Understanding the Visualization Map</h4>
                <ul>
                  <li><strong>Blue pulsing marker (draggable):</strong> Your test collar location. Drag to recalculate at new position.</li>
                  <li><strong>Blue wedge/sector:</strong> Shows planned drilling direction (azimuth) and directional weighting influence zone</li>
                  <li><strong>Shaded blue polygon:</strong> Actual area searched (may be larger if expanded)</li>
                  <li>
                    <strong>Colored dots:</strong> Historical collar locations, color-coded by their weight contribution:
                    <ul>
                      <li><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#22c55e;margin-right:6px;vertical-align:middle" aria-hidden="true"></span> Green = low weight (distant or misaligned)</li>
                      <li><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#eab308;margin-right:6px;vertical-align:middle" aria-hidden="true"></span> Yellow/Orange = moderate weight</li>
                      <li><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#ef4444;margin-right:6px;vertical-align:middle" aria-hidden="true"></span> Red/Magenta = high weight (close and aligned)</li>
                      <li><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#9ca3af;margin-right:6px;vertical-align:middle" aria-hidden="true"></span> Grey = zero weight (excluded from calculation)</li>
                    </ul>
                  </li>
                  <li><strong>Direction lines:</strong> Show the actual drilling azimuth of each historical hole (same color as dot)</li>
                  <li><strong>Info panel:</strong> Shows final radius used and number of historical holes found</li>
                  <li>
                    <strong>If radius expanded:</strong> Compare radius_used_m to your starting radius to 
                    see how far the search extended
                  </li>
                </ul>
              </div>

              <div className="info-section">
                <h4>Key Advantages</h4>
                <ul>
                  <li><strong>Spatially Aware:</strong> Radius-based search finds local deviation patterns</li>
                  <li><strong>Angle-Specific:</strong> Steep vs. shallow holes treated separately</li>
                  <li><strong>Depth-Aware:</strong> Compensates for increasing deviation with depth</li>
                  <li><strong>Direction-Aware:</strong> Weighted by drilling azimuth alignment (rock anisotropy)</li>
                  <li><strong>Distance-Aware:</strong> Prioritizes nearby holes with similar conditions</li>
                  <li><strong>Data-Driven:</strong> Based on actual historical deviation patterns, not assumptions</li>
                  <li><strong>Real-Time Updates:</strong> Materialized views can be refreshed with latest data</li>
                  <li><strong>Proven Accuracy:</strong> Pre-compensation significantly improves target intersection rates</li>
                </ul>
              </div>

              <div className="info-section">
                <h4>Deviation Rate Intervals</h4>
                <p>
                  <strong>What are they?</strong> Rate intervals allow you to see how quickly the drillhole deviates 
                  over specific depth increments, helping you understand deviation behavior at different scales.
                </p>
                <p><strong>How to use:</strong></p>
                <ul>
                  <li>Enter comma-separated values (e.g., <code>10,100</code> or <code>5,10,30,50,100</code>)</li>
                  <li>Each value represents a depth interval in meters</li>
                  <li>
                    <strong>Lift Rate:</strong> Change in dip angle per interval (positive = hole flattening, 
                    negative = hole steepening)
                  </li>
                  <li>
                    <strong>Drift Rate:</strong> Change in azimuth per interval (angular drift in horizontal plane)
                  </li>
                </ul>
                <p><strong>Practical use cases:</strong></p>
                <ul>
                  <li><strong>Short intervals (5-10m):</strong> Identify immediate deviation trends near collar</li>
                  <li><strong>Medium intervals (30-50m):</strong> Track deviation patterns through geological units</li>
                  <li><strong>Long intervals (100m):</strong> Understand overall trajectory and planning needs</li>
                </ul>
                <p className="info-note">
                  <IconText icon={ICON.uiTip} iconSize={16} as="span"><strong>Tip:</strong></IconText> Compare rates at different intervals to identify where deviation 
                  accelerates or stabilizes. This helps optimize survey frequency and compensation strategies.
                </p>
              </div>

              <div className="info-section">
                <h4>Data Quality & Filtering</h4>
                <p><strong>Historical data filtering:</strong></p>
                <ul>
                  <li>
                    <strong>Minimum hole depth:</strong> Only holes ≥50m are included in the historical dataset. 
                    Shallow holes often lack sufficient survey data for reliable deviation analysis.
                  </li>
                  <li>
                    <strong>Survey requirements:</strong> Holes must have at least 2 valid survey points (start and end) 
                    to calculate deviation deltas.
                  </li>
                  <li>
                    <strong>Spatial validity:</strong> Holes with invalid or missing collar coordinates are excluded.
                  </li>
                  <li>
                    <strong>Quality control:</strong> Outliers and holes with suspect survey data are filtered during 
                    materialized view refresh.
                  </li>
                </ul>
                <p className="info-warning">
                  <IconText icon={ICON.uiWarning} iconSize={16} as="span"><strong>Important:</strong></IconText> When n_pairs is low (&lt;10), results may be less reliable. 
                  Consider expanding the search radius or reviewing historical data coverage in that area.
                </p>
              </div>

              <div className="info-section">
                <h4>Database Functions</h4>
                <ul>
                  <li><strong>get_adjusted_angles_radius():</strong> Calculates compensated angles using radius search</li>
                  <li><strong>refresh_deviation_metrics():</strong> Updates materialized views with latest drilling data</li>
                  <li><strong>Schema:</strong> drillhole_deviation (EPSG:26908)</li>
                </ul>
              </div>

              <div className="info-section">
                <h4>Data Sources</h4>
                <ul>
                  <li>Historical surveys: drilling_historic.survey</li>
                  <li>Collar geometry: drilling_historic.collar</li>
                  <li>Deviation metrics: Materialized views in drillhole_deviation schema</li>
                </ul>
              </div>
            </div>
          )}


          <form onSubmit={handleCalculate} className="calculator-form">
            {/* Row 1: Collar coordinates and orientation */}
            <div className="form-row form-row-4">
              <div className="form-group">
                <label htmlFor="easting">Collar Easting (UTM)</label>
                <input
                  type="number"
                  id="easting"
                  name="easting"
                  value={formData.easting}
                  onChange={handleInputChange}
                  step="0.1"
                  required
                  disabled={calculating}
                />
              </div>

              <div className="form-group">
                <label htmlFor="northing">Collar Northing (UTM)</label>
                <input
                  type="number"
                  id="northing"
                  name="northing"
                  value={formData.northing}
                  onChange={handleInputChange}
                  step="0.1"
                  required
                  disabled={calculating}
                />
              </div>

              <div className="form-group">
                <label htmlFor="azimuth">Planned Azimuth (0-360°)</label>
                <input
                  type="number"
                  id="azimuth"
                  name="azimuth"
                  value={formData.azimuth}
                  onChange={handleInputChange}
                  step="0.1"
                  min="0"
                  max="360"
                  required
                  disabled={calculating}
                />
              </div>

              <div className="form-group">
                <label htmlFor="dip">Planned Dip (-90 to 90°)</label>
                <input
                  type="number"
                  id="dip"
                  name="dip"
                  value={formData.dip}
                  onChange={handleInputChange}
                  step="0.1"
                  min="-90"
                  max="90"
                  required
                  disabled={calculating}
                />
              </div>
            </div>

            {/* Row 2: Depth and search parameters */}
            <div className="form-row form-row-3">
              <div className="form-group">
                <label htmlFor="depth">Target Depth (meters)</label>
                <input
                  type="number"
                  id="depth"
                  name="depth"
                  value={formData.depth}
                  onChange={handleInputChange}
                  step="0.1"
                  min="0"
                  required
                  disabled={calculating}
                />
              </div>

              <div className="form-group">
                <label htmlFor="searchRadius">
                  Search Radius (meters)
                  <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: '0.75rem', marginLeft: '0.5rem' }}>
                    (optional, default: 500m)
                  </span>
                </label>
                <input
                  type="number"
                  id="searchRadius"
                  name="searchRadius"
                  value={formData.searchRadius}
                  onChange={handleInputChange}
                  placeholder="500 (default)"
                  step="0.1"
                  min="0"
                  disabled={calculating}
                />
              </div>

              <div className="form-group">
                <label htmlFor="minPairs">Minimum Historical Pairs</label>
                <input
                  type="number"
                  id="minPairs"
                  name="minPairs"
                  value={formData.minPairs}
                  onChange={handleInputChange}
                  step="1"
                  min="1"
                  required
                  disabled={calculating}
                />
              </div>
            </div>

            {/* Expand Mode Toggle */}
            <div className="expand-mode-section">
              <label className="expand-toggle">
                <input
                  type="checkbox"
                  name="expand"
                  checked={formData.expand}
                  onChange={handleInputChange}
                  disabled={calculating}
                />
                <span className="expand-label">Enable Radius Expansion</span>
                <span className="expand-hint">
                  Automatically expand search radius until minimum pairs are found
                </span>
              </label>

              {/* Conditional Expand Parameters */}
              {formData.expand && (
                <div className="expand-params">
                  <div className="form-group">
                    <label htmlFor="step">Expansion Step (meters)</label>
                    <input
                      type="number"
                      id="step"
                      name="step"
                      value={formData.step}
                      onChange={handleInputChange}
                      step="0.1"
                      min="0"
                      required
                      disabled={calculating}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="maxRadius">Maximum Radius (meters)</label>
                    <input
                      type="number"
                      id="maxRadius"
                      name="maxRadius"
                      value={formData.maxRadius}
                      onChange={handleInputChange}
                      step="0.1"
                      min="0"
                      required
                      disabled={calculating}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Weighting Controls */}
            <div className="weighting-section">
              <h4 className="section-heading">Weighting Controls</h4>
              
              <label className="weight-toggle">
                <input
                  type="checkbox"
                  name="useDirectionalWeight"
                  checked={formData.useDirectionalWeight}
                  onChange={handleInputChange}
                  disabled={calculating}
                />
                <span className="toggle-label">Enable Directional Weighting</span>
              </label>

              {formData.useDirectionalWeight && (
                <div className="slider-control">
                  <label htmlFor="dirExponent" className="slider-label">
                    Angular Exponent
                    <span className="slider-value-display">{formData.dirExponent.toFixed(1)}</span>
                  </label>
                  <input
                    type="range"
                    id="dirExponent"
                    name="dirExponent"
                    min="0"
                    max="5"
                    step="0.1"
                    value={formData.dirExponent}
                    onChange={handleInputChange}
                    disabled={calculating}
                  />
                  <div className="slider-hint">
                    Higher values prioritize neighbors in the drilling direction
                  </div>
          </div>
        )}

              <label className="weight-toggle">
                <input
                  type="checkbox"
                  name="useDistanceWeight"
                  checked={formData.useDistanceWeight}
                  onChange={handleInputChange}
                  disabled={calculating}
                />
                <span className="toggle-label">Enable Distance Weighting</span>
              </label>
            </div>

            {/* Rate Intervals Configuration */}
            <div className="rate-intervals-section">
              <div className="form-group">
                <label htmlFor="rateIntervals">
                  Rate Intervals (meters) 
                  <span className="hint">(comma-separated, e.g., 10,100)</span>
                </label>
                <input
                  type="text"
                  id="rateIntervals"
                  name="rateIntervals"
                  value={formData.rateIntervals}
                  onChange={handleInputChange}
                  placeholder="10,100"
                  disabled={calculating}
                />
                <div className="input-hint">
                  Specifies depth intervals for calculating lift and drift rates (e.g., "10,100" calculates rates per 10m and per 100m)
                </div>
              </div>
            </div>

            <div className="button-group">
              <Button 
                type="submit" 
                variant="primary" 
                disabled={calculating}
                loading={calculating}
              >
                {calculating ? 'Calculating...' : (<IconText icon={ICON.uiFilter} iconSize={16}>Calculate Compensated Values</IconText>)}
              </Button>
              <Button 
                type="button" 
                variant="secondary" 
                onClick={handleReset}
                disabled={calculating}
              >
                <IconText icon={ICON.uiRefresh} iconSize={16}>Reset</IconText>
              </Button>
            </div>
          </form>
        </Card>

        {/* Error Display */}
        {error && (
          <Card className="error-card">
            <IconText icon={ICON.uiError} as="h3">Calculation Error</IconText>
            <p>{error}</p>
            {error.includes('Forbidden') && (
              <p className="hint">
                You may not have permission to access this feature. Contact your administrator 
                to be added to the wlfwkr-deviation-wizard security group.
              </p>
            )}
          </Card>
        )}

        {/* Results Display */}
        {result && result.success && (
          <Card className="results-card">
            <IconText icon={ICON.uiChartAnalytics} as="h2">Pre-Compensated Drillhole Parameters</IconText>
            
            <div className="results-grid">
              {/* Input Values */}
              <div className="result-section">
                <h3>Planned Values</h3>
                <div className="result-item">
                  <span className="result-label">Easting:</span>
                  <span className="result-value">{result.input.easting.toFixed(2)} m</span>
                </div>
                <div className="result-item">
                  <span className="result-label">Northing:</span>
                  <span className="result-value">{result.input.northing.toFixed(2)} m</span>
                </div>
                <div className="result-item">
                  <span className="result-label">Azimuth:</span>
                  <span className="result-value">{result.input.az_deg.toFixed(2)}°</span>
                </div>
                <div className="result-item">
                  <span className="result-label">Dip:</span>
                  <span className="result-value">{result.input.dip_deg.toFixed(2)}°</span>
                </div>
                <div className="result-item">
                  <span className="result-label">Target Depth:</span>
                  <span className="result-value">{result.input.depth_m.toFixed(1)} m</span>
                </div>
              </div>

              {/* Compensated Values */}
              <div className="result-section">
                <h3>Compensated Values (Use These)</h3>
                {result.result && (result.result.az_adj !== undefined || result.result.adjust_az_dip_offsets_hexbin_at_xy) ? (
                  (() => {
                    // Handle both new and legacy response formats
                    let compensatedAz, compensatedDip;
                    
                    if (result.result.az_adj !== undefined) {
                      // New format
                      compensatedAz = result.result.az_adj;
                      compensatedDip = result.result.dip_adj;
                    } else {
                      // Legacy format (array)
                      const compensatedValues = result.result.adjust_az_dip_offsets_hexbin_at_xy;
                      compensatedAz = compensatedValues[0];
                      compensatedDip = compensatedValues[1];
                    }
                    
                    const azAdjustment = compensatedAz - result.input.az_deg;
                    const dipAdjustment = compensatedDip - result.input.dip_deg;
                    
                    return (
                      <>
                        <div className="result-item highlight">
                          <span className="result-label">Compensated Azimuth:</span>
                          <span className="result-value">
                            {compensatedAz.toFixed(2)}°
                          </span>
                        </div>
                        <div className="result-item highlight">
                          <span className="result-label">Compensated Dip:</span>
                          <span className="result-value">
                            {compensatedDip.toFixed(2)}°
                          </span>
                        </div>
                        <div className="result-item">
                          <span className="result-label">Azimuth Adjustment:</span>
                          <span className="result-value correction">
                            {azAdjustment >= 0 ? '+' : ''}{azAdjustment.toFixed(2)}°
                          </span>
                        </div>
                        <div className="result-item">
                          <span className="result-label">Dip Adjustment:</span>
                          <span className="result-value correction">
                            {dipAdjustment >= 0 ? '+' : ''}{dipAdjustment.toFixed(2)}°
                          </span>
                        </div>
                      </>
                    );
                  })()
                ) : (
                  <p className="no-result">No compensation data available</p>
                )}
              </div>
            </div>

            {/* Metadata Section */}
            {result.result && result.result.az_adj !== undefined && (
              <div className="metadata-section">
                <IconText icon={ICON.uiChartAnalytics} as="h3">Calculation Metadata</IconText>
                <div className="metadata-grid">
                  <div className="metadata-item">
                    <span className="metadata-label">Dip Bin:</span>
                    <span className="metadata-value">{result.result.dip_bin_key || 'N/A'}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Depth Bin:</span>
                    <span className="metadata-value">{result.result.depth_bin_key || 'N/A'}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Search Radius:</span>
                    <span className="metadata-value">{result.result.radius_used_m || 'N/A'} m</span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Historical Pairs:</span>
                    <span className="metadata-value">{result.result.n_pairs || 0}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Median Δ Dip:</span>
                    <span className="metadata-value">
                      {result.result.median_delta_dip != null 
                        ? result.result.median_delta_dip.toFixed(3) + '°' 
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Mean Δ Azimuth:</span>
                    <span className="metadata-value">
                      {result.result.mean_delta_az_deg != null 
                        ? result.result.mean_delta_az_deg.toFixed(3) + '°' 
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Interval-Based Rates */}
            {result.result && (result.result.lift_rate_deg_by_interval || result.result.drift_rate_deg_by_interval) && (
              <div className="interval-rates-section">
                <IconText icon={ICON.uiChartAnalytics} as="h3">Deviation Rates by Depth Interval</IconText>
                <div className="interval-rates-grid">
                  {result.result.lift_rate_deg_by_interval && (
                    <div className="rate-group">
                      <h4>Lift Rate (Dip Change)</h4>
                      <div className="rate-items">
                        {Object.entries(
                          typeof result.result.lift_rate_deg_by_interval === 'string'
                            ? JSON.parse(result.result.lift_rate_deg_by_interval)
                            : result.result.lift_rate_deg_by_interval
                        ).map(([interval, rate]) => (
                          <div key={interval} className="rate-item">
                            <span className="rate-interval">Per {interval}m:</span>
                            <span className="rate-value">{parseFloat(rate).toFixed(4)}°</span>
                          </div>
                        ))}
                </div>
                    </div>
                  )}
                  {result.result.drift_rate_deg_by_interval && (
                    <div className="rate-group">
                      <h4>Drift Rate (Azimuth Change)</h4>
                      <div className="rate-items">
                        {Object.entries(
                          typeof result.result.drift_rate_deg_by_interval === 'string'
                            ? JSON.parse(result.result.drift_rate_deg_by_interval)
                            : result.result.drift_rate_deg_by_interval
                        ).map(([interval, rate]) => (
                          <div key={interval} className="rate-item">
                            <span className="rate-interval">Per {interval}m:</span>
                            <span className="rate-value">{parseFloat(rate).toFixed(4)}°</span>
                    </div>
                  ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Map Visualization */}
            {mapData && mapData.collarPoint && (
              <div className="map-visualization-section">
                <IconText icon={ICON.uiLocationPin} as="h3">Search Area Visualization</IconText>{autoCalculating && <span className="auto-calc-badge">Recalculating...</span>}
                <DeviationWizardMap
                  collarPoint={mapData.collarPoint}
                  searchPolygon={mapData.searchPolygon}
                  neighbors={mapData.neighbors}
                  radiusUsed={result.result.radius_used_m}
                  nPairs={result.result.n_pairs}
                  metadata={{
                    ...result.result,
                    easting: result.input.easting,
                    northing: result.input.northing,
                    az_in: result.input.az_deg,
                    dip_in: result.input.dip_deg
                  }}
                  onPointSelect={handleMapPointSelect}
                />
                </div>
              )}

            {/* Raw Result for Debugging */}
            <details className="raw-result">
              <summary>View Raw API Response</summary>
              <pre>{JSON.stringify(result, null, 2)}</pre>
            </details>
            </Card>
        )}
      </div>
    </Layout>
  );
};

export default DeviationWizard;
